import type { TWidget } from 'widgets/Widget';
import type { TRefElementInstance } from 'widgets/toolbox/RefElement';

/**
 * @description Base CheckoutButtons implementation
 * @param Widget Base widget for extending
 * @returns Checkout Buttons (PayPal, ApplePay, etc.) class
 */
function CheckoutButtonsClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory cart
     * @class CheckoutButtons
     * @augments Widget
     * @classdesc Represents CheckoutButtons component for making emit after initialization
     * @example
     *
     * <div
     *     data-widget="checkoutButtons"
     *     data-widget-event-click="proceedToCheckout"
     *     data-widget-event-error="showError"
     *     data-tau="cart_top_section"
     *     class="b-proceed_checkout"
     * >
     *     <isinclude template="m/cart/checkoutButtons" />
     *     {{${'#'}totals.grandTotal.value}}
     *          <isif condition="${require('util/pref').is('applepay.enabled') && session.custom.applepaysession}">
     *              <p data-ref="delimiter" class="b-proceed_checkout-or" hidden>
     *                  ${Resource.msg('common.or', 'common', null)}
     *              </p>
     *          </isif>
     *          <isinclude template="${require('util/pref').if('applepay.enabled', 'm/cart/applePayButtonCart', 'util/empty')}" />
     *     {{/totals.grandTotal.value}}
     * </div>
     */
    class CheckoutButtons extends Widget {
        /**
         * @description Show text -'OR' when one of payment method was initialized
         */
        showDelimiter(): void {
            this.ref('delimiter').show();
        }

        /**
         * @description Emit click event with sent params
         * @param btn - clicked button
         * @param event - target event
         * @emits CartMgr#click
         */
        proceedToCheckout(btn: TRefElementInstance, event: Event): void {
            this.emitWithoutContext('click', btn, event);
        }

        /**
         * @description Emit error event with sent params
         * @param widget - widget
         * @param error - error
         * @emits CartMgr#error
         */
        showError(widget: TWidget, error: Event): void {
            this.emitWithoutContext('error', widget, error);
        }
    }

    return CheckoutButtons;
}

export type TCheckoutButtons = ReturnType<typeof CheckoutButtonsClassCreator>;

export type TCheckoutButtonsInstance = InstanceType<TCheckoutButtons>;

export default CheckoutButtonsClassCreator;
